import { NbaChannel } from '@/src/types/NextBestAction';
import { ProductId } from '@/src/types/Products';
import { CustomerClassificationQueryParams } from '../api/use-customer-classification-query';

export const RESET_PASSWORD_URL = process.env.NX_PUBLIC_RESET_PASSWORD_URL;
export const SMART_BOOKING_URL = process.env.NX_PUBLIC_SMART_BOOKING_URL;
export const GEMINI_URL = process.env.NX_PUBLIC_GEMINI_URL;
export const GEMINI_API_URL = process.env.NX_PUBLIC_GEMINI_API_URL;

export const KAPI_URL = process.env.NX_PUBLIC_KAPI_URL;
export const TOKEN_URL = process.env.NX_PUBLIC_TOKEN_URL;
export const LOGOUT_URL = process.env.NX_PUBLIC_LOGOUT_URL ?? '';
export const BETA_WEBSITE_URL = process.env.NX_PUBLIC_BETA_WEBSITE_URL ?? '';
export const NEW_WEB_SMART_BOOKING_URL = `${BETA_WEBSITE_URL}/meter-readings/smart-meter`;

const KONG_API_ROOT = process.env.NX_PUBLIC_KONG_BASE_URL;
const CONTRACTS_API_ROOT = process.env.NX_PUBLIC_CONTRACTS_API_URL;
const OREX_APIS_ROOT = `${KONG_API_ROOT}/orex/api`;
const PRODUCTS_API_ROOT = `${KONG_API_ROOT}/green`;
const PRODUCTS_AND_TARIFFS_ROOT =
  process.env.NX_PUBLIC_PRODUCTS_AND_TARIFFS_URL;
const USAGE_ROOT = `${KONG_API_ROOT}/usage`;
const NBA_ROOT = `${KONG_API_ROOT}/nba`;
const ENDPOINT_MOSS_API = `${process.env.NX_PUBLIC_MOSS_API_URL}/api`;
const ENDPOINT_CARBON_ZERO_BFF = `${KONG_API_ROOT}/carbon-bff`;
export const createContractsUrl = (accountId: string): string =>
  `${CONTRACTS_API_ROOT}/v1/customer?accounts=${accountId}&types=energy`;
export const ENDPOINT_TARIFF_COSTS = `${OREX_APIS_ROOT}/tariff-costs`;
export const createTariffCostsUrl = (accountId: string): string =>
  `${OREX_APIS_ROOT}/tariff-costs/${accountId}`;
export const CHARGE_ANYTIME_API_ROOT = process.env.NX_PUBLIC_CHARGE_ANYTIME_URL;

export const USAGE_ROUTES = {
  HALF_HOURLY_USAGE: `${USAGE_ROOT}/api/half-hourly`,
  DAILY_USAGE: `${USAGE_ROOT}/api/daily`,
  MONTHLY_USAGE: `${USAGE_ROOT}/api/monthly`,
};

export const createProductsEndpoint = (accountId: string): string =>
  `${PRODUCTS_API_ROOT}/account/${accountId}/products`;
export const createProductsEndpointV2 = (accountId: string): string =>
  `${PRODUCTS_API_ROOT}/v2/account/${accountId}/products`;
/**
 * @deprecated deprecated endpoint, use /v2/account/{accountId} instead
 */
export const createProductActivateEndpoint = (accountId: string): string =>
  `${PRODUCTS_API_ROOT}/account/${accountId}`;
export const createProductActivateEndpointV2 = (accountId: string): string =>
  `${PRODUCTS_API_ROOT}/v2/account/${accountId}`;
export const createProductActivateEndpointV3 = (accountId: string): string =>
  `${PRODUCTS_API_ROOT}/v3/account/${accountId}`;
export const createProductCatalogueEndpoint = (
  accountId: string,
  promoCode?: string,
): string =>
  `${PRODUCTS_API_ROOT}/account/${accountId}/catalogue${
    promoCode ? `?promoCode=${promoCode}` : ''
  }`;
export const createProductCatalogueEndpointV2 = (
  accountId?: string,
  promoCode?: string,
): string =>
  `${PRODUCTS_API_ROOT}/v2/account/${accountId}/catalogue${
    promoCode ? `?promoCode=${promoCode}` : ''
  }`;
export const createProductCancelEndpoint = (
  accountId: string,
  productId: ProductId,
  versionId?: string,
): string => {
  const baseEndpoint = `${PRODUCTS_API_ROOT}/account/${accountId}/products/${productId}`;

  if (versionId) return `${baseEndpoint}?versionId=${versionId}`;
  return baseEndpoint;
};
export const createPlansEndpoint = (accountId: string) =>
  `${OREX_APIS_ROOT}/plans/${accountId}`;
export const createAvailablePlansEndpoint = (accountId: string) =>
  `${OREX_APIS_ROOT}/available-plans/${accountId}`;
export const createRenewablePlansEndpoint = (accountId: string) =>
  `${OREX_APIS_ROOT}/renewable-plans/${accountId}`;
export const createRenewPlanEndpoint = (serviceAccountId: string) =>
  `${OREX_APIS_ROOT}/contracts/${serviceAccountId}/renew`;

export const createProjectedCostsEndpoint = (accountId: string): string =>
  `${KONG_API_ROOT}/pace/recommended-dds/v1/${accountId}/projected-costs`;

export const createRecommendedDirectDebitEndpoint = (
  accountId: string,
): string =>
  `${KONG_API_ROOT}/pace/recommended-dds/v1/${accountId}/recommended-direct-debit`;

export const createGetDirectDebitEndpoint = (accountId: string): string =>
  `${KONG_API_ROOT}/pace/payments/v1/${accountId}/direct-debit`;

export const createHalfHourlyUsageEndpoint = (
  accountId: string,
  date: string,
): string => `${USAGE_ROUTES.HALF_HOURLY_USAGE}/${accountId}?date=${date}`;

export const createDailyUsageEndpoint = (
  accountId: string,
  date: string,
): string => `${USAGE_ROUTES.DAILY_USAGE}/${accountId}?date=${date}`;

export const createMonthlyUsageEndpoint = (
  accountId: string,
  date: string,
): string => `${USAGE_ROUTES.MONTHLY_USAGE}/${accountId}?date=${date}`;

export const createAccountInfoEndpoint = (accountId: string) =>
  `${OREX_APIS_ROOT}/account/${accountId}/info`;

export const createMoveOutEndpoint = (accountId: string) =>
  `${KONG_API_ROOT}/homemoves/v1/move-out-date/${accountId}`;

const BAL_API_ROOT = `${KONG_API_ROOT}/bal`;
export const ENDPOINT_BILLING_GRAPHQL = `${BAL_API_ROOT}/api/graphql`;
export const createFinalStatementEndpoint = (accountId: string) =>
  `${BAL_API_ROOT}/final-statement-state/account/${accountId}`;

/* The statement endpoints rely on url rewrite that don't exist in Kong
 * https://github.com/ovotech/orion-ui/tree/master/nginx
 */

export const createGetSmartMeterBookingEndpoint = (accountId: string) =>
  `${KONG_API_ROOT}/api/v2/marketing?accountId=${accountId}&jobType=Credit`;

export const createGetSmartMeterBookabilityEndpoint = (accountId: string) =>
  `${KONG_API_ROOT}/v2/smart-meter-booking/bookability/${accountId}`;

export const createGetShowSmartMeterBookingBannerEndpoint = (
  accountId: string,
) =>
  `${KONG_API_ROOT}/api/v2/marketing/banner?jobType=Credit&accountId=${accountId}`;

export const createGetNextBestActionEndpoint = (
  accountId: string,
  channel: NbaChannel,
) =>
  `${NBA_ROOT}/get_next_best_actions?ACCOUNT_NO=${accountId}&CHANNEL=${channel}`;

export const createPostNextBestActionEndpoint = `${NBA_ROOT}/post_action_response`;

export const ENDPOINT_ADDRESS_LOOKUP = `${KONG_API_ROOT}/address/address`;

export const createAddressLookupEndpoint = (postcode: string) =>
  `${GEMINI_API_URL}/address-lookup?provider=gbgv2&postcode=${postcode}`;

export const createHomeMoveCustomerEndpoint = (customerId: string) =>
  `${GEMINI_API_URL}/home-moves/${customerId}`;

export const createIsTuwyCustomerEndpoint = (customerId: string) =>
  `${GEMINI_API_URL}/kapi/is-tuwy-customer/${customerId}`;

export const createRenewalEligibilityEndpoint = (
  accountId: string,
  { includeContracts = false }: { includeContracts?: boolean } = {},
) =>
  `${GEMINI_API_URL}/energy/customer/${accountId}/renewal-eligibility${
    includeContracts ? '?includeContracts=true' : ''
  }`;

export const createEnergyQuoteEligibilityUrl = (params: URLSearchParams) =>
  `${GEMINI_API_URL}/quote/v2/eligibility?${params.toString()}`;

export const ACCOUNT_EXPERIENCE_FAQS =
  'https://www.ovoenergy.com/new-online-account-experience-faq';

export const ENDPOINT_CARD_PAYMENT = `${ENDPOINT_MOSS_API}/v1/payments/cards/payment-session`;

export const createCollectableBalanceEndpoint = (accountId: string) =>
  `${ENDPOINT_MOSS_API}/v1/account/${accountId}/collectable-balance`;

export const OVO_QUOTE_AND_SWITCH = 'https://switch.ovoenergy.com';

// This endpoint returns a list of products associated with the mandatory ids query parameter.
export const createAvailableProductsEndpoint = (
  productId: string,
  versionId?: string | null,
  promoCredit?: string | null,
): string => {
  if (promoCredit && versionId) {
    return `${PRODUCTS_AND_TARIFFS_ROOT}/v4/products?id=${productId}&id=${versionId}&promoCredit=${promoCredit}`;
  }
  if (promoCredit) {
    return `${PRODUCTS_AND_TARIFFS_ROOT}/v4/products?id=${productId}&promoCredit=${promoCredit}`;
  }
  let ids = productId;
  if (versionId) ids = `${productId},${versionId}`;
  return `${PRODUCTS_AND_TARIFFS_ROOT}/v3/products?ids=${ids}`;
};

// Product Service lists every live version; one stays effective until a
// later version's activeFrom passes.
export const createCurrentProductsEndpointV5 = (): string =>
  `${PRODUCTS_AND_TARIFFS_ROOT}/v5/products/current`;

const carbonZeroBffUrlWithAccountId = (path: string, accountId: string) =>
  `${ENDPOINT_CARBON_ZERO_BFF}/${accountId}/${path}`;

export const createMetersEndpoint = (accountId: string): string =>
  `${ENDPOINT_MOSS_API}/v1/account/${accountId}/meters`;

export const carbonZeroBffEndpoints = {
  getCarbonIntensity: `${ENDPOINT_CARBON_ZERO_BFF}/carbonintensity`,

  homeProfileWidget: (accountId: string) =>
    `${ENDPOINT_CARBON_ZERO_BFF}/${accountId}/home-profile-widget`,

  putHomeProfile: (accountId: string) =>
    `${ENDPOINT_CARBON_ZERO_BFF}/${accountId}/home-profile`,

  getHub: (accountId: string) =>
    carbonZeroBffUrlWithAccountId('hub', accountId),

  putHalfHourlyOptIn: (accountId: string) =>
    `${ENDPOINT_CARBON_ZERO_BFF}/${accountId}/usage/half-hourly-opt-in`,

  getBreakdownPage: (accountId: string) =>
    carbonZeroBffUrlWithAccountId('web/breakdown-page', accountId),

  getWebNavigation: (accountId: string) =>
    carbonZeroBffUrlWithAccountId('web/navigation', accountId),
};

export const createHomePlanUrl = (
  source?: string | null,
  accountId?: string | null,
  extra?: string,
) => {
  const url =
    process.env.NX_PUBLIC_HOMEPLAN_URL +
    (!source ? '' : `?source=${source}`) +
    (!accountId ? '' : `&accountId=${accountId}`);

  if (extra) {
    return url + extra;
  }

  return url;
};

export const createHomeRecoverUrl = (
  source: string,
  accountId?: string | null,
) =>
  `${process.env.NX_PUBLIC_HOMEPLAN_URL}/home-recover/?source=${source}` +
  (!accountId ? '' : `&accountId=${accountId}`);

const buildClassificationQueryParams = (
  props: CustomerClassificationQueryParams,
) => {
  const filteredProps = Object.fromEntries(
    Object.entries(props).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

  return new URLSearchParams(filteredProps as URLSearchParams).toString();
};

export const createCustomerClassificationUrl = (
  props: CustomerClassificationQueryParams,
) =>
  `${
    process.env.NX_PUBLIC_GROWTH_HOMESERVICES_CLASSIFICATION_URL
  }/v1/classify?${buildClassificationQueryParams(props)}`;

export const createCustomerClassificationV2Url = (
  props: CustomerClassificationQueryParams,
) =>
  `${
    process.env.NX_PUBLIC_GROWTH_HOMESERVICES_CLASSIFICATION_URL
  }/v2/classify?${buildClassificationQueryParams(props)}`;

export const BASS_API_URL = `${process.env.NX_PUBLIC_BASS_BASE_URL}/graphql`;

export const createChargeAnytimeActivationUrl = () =>
  `${CHARGE_ANYTIME_API_ROOT}/v1/acquisition`;

export const createChargeAnytimeDevicesUrl = (
  deviceType: 'all' | 'vehicles' | 'chargers',
) => `${CHARGE_ANYTIME_API_ROOT}/v1/devices/${deviceType}`;

export const createAddressLookupBySearchIdEndpoint = (searchId: string) =>
  `${GEMINI_API_URL}/address-by-search-id?propertySearchId=${searchId}`;

export const createBeyondRewardsEndpoint = (accountId: string): string =>
  `${process.env.NX_PUBLIC_BEYOND_REWARDS_API_URL}/account-bolton?accountId=${accountId}`;

export const createHomeplanQuotesURL = () =>
  `${process.env.NX_PUBLIC_QUOTES_SERVICE_URL}`;

const PAYG_BASE_URL = process.env.NX_PUBLIC_PAYG_TOPUP_FULFILLMENT_API_URL;
export const paygEndpoints = {
  config: (accountId: string) =>
    `${PAYG_BASE_URL}/accounts/${accountId}/channels/ovo_account_web/config`,
  balances: (accountId: string) =>
    `${PAYG_BASE_URL}/v2/accounts/${accountId}/balances`,
  topups: (accountId: string) =>
    `${PAYG_BASE_URL}/accounts/${accountId}/topups`,
  pendingTopups: (accountId: string) =>
    `${PAYG_BASE_URL}/accounts/${accountId}/topups/pending`,
};
